import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query } from '@firebase/firestore';
import { db } from '../config/firebase';
import { InventoryItem, InventoryLogBase, InventoryLogStored } from '../models/Inventory';
import { prefixCollection } from '../utils/prefix';
import { adjustNumberFieldWithLogToSubcollection, createItem, deleteItem, fetchItems, updateItem } from './generic';

export const fetchInventoryItems = async (tenant?: string): Promise<InventoryItem[]> => {
  return fetchItems<InventoryItem>(prefixCollection('inventory', tenant));
};

export const fetchInventoryItem = async (id: string, tenant?: string): Promise<InventoryItem | null> => {
  const ref = doc(db, prefixCollection('inventory', tenant), id);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...(snap.data() as Omit<InventoryItem, 'id'>) } : null;
};

export const fetchInventoryLogs = async (id: string, tenant?: string): Promise<InventoryLogStored[]> => {
  const base = prefixCollection('inventory', tenant);
  const qy = query(collection(db, base, id, 'logs'), orderBy('timestamp', 'desc'));
  const snap = await getDocs(qy);

  // Firestore resolves serverTimestamp() to Timestamp on reads
  return snap.docs.map((d) => ({ ...(d.data() as InventoryLogStored) }));
};

export const fetchInventoryItemWithLogs = async (
  id: string,
  tenant?: string,
): Promise<(InventoryItem & { log: InventoryLogStored[] }) | null> => {
  const item = await fetchInventoryItem(id, tenant);
  if (!item) return null;
  const log = await fetchInventoryLogs(id, tenant);
  return { ...item, log };
};

export const createInventoryItem = async (item: Omit<InventoryItem, 'id'>, tenant?: string): Promise<string> => {
  const created = await createItem<InventoryItem>(prefixCollection('inventory', tenant), item);
  return created.id;
};

export const updateInventoryItem = async (item: InventoryItem, tenant?: string): Promise<void> => {
  await updateItem<InventoryItem>(prefixCollection('inventory', tenant), item.id, item);
};

export const adjustInventoryQuantity = async (
  id: string,
  {
    operation,
    quantity,
    action,
    userId,
    batchId,
  }: {
    operation: InventoryLogBase['operation'];
    quantity: number;
    action: InventoryLogBase['action'];
    userId?: string;
    batchId?: string;
  },
  tenant?: string,
): Promise<void> => {
  const collectionName = prefixCollection('inventory', tenant);

  await adjustNumberFieldWithLogToSubcollection<InventoryItem>(
    collectionName,
    id,
    'quantity',
    operation,
    quantity,
    (currentQuantity) => ({
      quantity,
      currentQuantity,
      userId,
      action,
      batchId,
    }),
  );
};

export const deleteInventoryItem = async (id: string, tenant?: string): Promise<void> => {
  await deleteItem(prefixCollection('inventory', tenant), id);
};

export const subscribeInventoryLogs = (
  id: string,
  tenant: string | undefined,
  onChange: (logs: InventoryLogStored[]) => void,
  onError: (e: Error) => void,
) => {
  const base = prefixCollection('inventory', tenant);
  const qy = query(collection(db, base, id, 'logs'), orderBy('timestamp', 'desc'));
  return onSnapshot(
    qy,
    (snap) => onChange(snap.docs.map((d) => d.data() as InventoryLogStored)),
    (err) => onError(err as Error),
  );
};

export function formatLogLine(log: InventoryLogStored, userName: string, unit: string): string {
  const qty = `${log.quantity} ${unit}`;
  const current = `${log.currentQuantity} ${unit}`;

  switch (log.action) {
    case 'initial_stock':
      return `${userName} set the initial stock to ${current}.`;

    case 'inventory_check':
      return `${userName} confirmed stock at ${current} during an inventory check.`;

    case 'inventory_adjustment':
      return `${userName} manually set the quantity to ${current}.`;

    case 'inventory_restock':
      return `${userName} added ${qty}, now ${current}.`;

    case 'inventory_depletion':
      return `${userName} removed ${qty}, now ${current}.`;

    case 'inventory_transfer':
      return `${userName} transferred ${qty}, now ${current}.`;

    default:
      return `${userName} updated stock to ${current}.`;
  }
}
